import {
    Avatar,
    Badge,
    Box,
    Button,
    HStack,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
    useColorModeValue,
    useDisclosure
} from '@chakra-ui/react';
import React from 'react';
import ErrorState from '../../components/states/ErrorState';
import PageCard from '../../components/page-card';
import { translate } from '../../i18n';
import { CoachingSessionDto, CoachingSessionStatus } from '../../models/coach/dto/coaching-session.dto';
import { paramFromWindowLocation } from '../../utils/url-parser';
import { EditCoachingSessionCourtModal } from './components/EditCoachingSessionCourtModal';
import PaymentIntents from './components/PaymentIntents';
import { useCancelCoachingSession } from './hooks/useCancelCoachingSession';
import { useGetCoachingSession } from './hooks/useGetCoachingSession';
import { useIsOrgManager } from '../auth/roles';
import { useCanManageCoach } from '../coach-management/hooks/useCanManageCoach';

export default function CoachingSessionDetailsPage() {
    const sessionId = paramFromWindowLocation('coachingSession');
    // Coaches open their own sessions from the calendar. Payment intents are
    // an admin-only read (403 for coaches) and court reassignment is
    // admin-only. Cancel stays for the coach's OWN session — that is the
    // deliberate self-service rail (API: hasCoachSelfOrOrganizationAdminAccess)
    // — but Calendrier renders EVERY coach's sessions, so on a colleague's
    // session the button was an action the API refuses. `useCanManageCoach`
    // is the same predicate the API authorizes with.
    const isOrgManager = useIsOrgManager();
    const { data: session, isLoading, error, refetch } = useGetCoachingSession(sessionId);
    const canManageThisSession = useCanManageCoach(session?.coach?.id);
    const cancelSessionMutation = useCancelCoachingSession();
    const { isOpen: isEditCourtOpen, onOpen: onEditCourtOpen, onClose: onEditCourtClose } = useDisclosure();

    if (isLoading && !session) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error || !session) {
        return (
            <ErrorState message={translate('coaching_session.error_loading')} onRetry={refetch} />
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            <PageCard
                showBackButton
                title={translate('coaching_session.details')}
            >
                <CoachingSessionInformation
                    session={session}
                    cancelSessionMutation={canManageThisSession ? cancelSessionMutation : undefined}
                    onEditCourt={isOrgManager ? onEditCourtOpen : undefined}
                />
            </PageCard>

            {isOrgManager && (
                <PageCard title={translate('game.payment_status')}>
                    <PaymentIntents coachingSessionId={session.id} />
                </PageCard>
            )}

            <EditCoachingSessionCourtModal
                isOpen={isEditCourtOpen}
                onClose={onEditCourtClose}
                session={session}
            />
        </VStack>
    );
}

interface CoachingSessionInformationProps {
    session: CoachingSessionDto;
    /**
     * Optional, and the optionality is load-bearing: this component
     * dereferences the mutation DURING RENDER (`isLoading`), and
     * `dashboard/tsconfig.json` has `strictNullChecks: false`, so a required
     * prop passed as `undefined` type-checks and then throws a TypeError at
     * runtime — which, with no ErrorBoundary anywhere in `dashboard/src`,
     * unmounts the whole SPA. That is exactly how the coach's game-details
     * landing page was a white screen for 17 runs.
     */
    cancelSessionMutation?: ReturnType<typeof useCancelCoachingSession>;
    onEditCourt?: () => void;
}

export const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('en', { month: 'short' });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} - ${hours}:${minutes}`;
};

export const formatDuration = (startTime: string, endTime: string) => {
    const durationMs = new Date(endTime).getTime() - new Date(startTime).getTime();
    const minutes = Math.round(durationMs / 60000);
    return `${minutes} ${translate('coaching_session.minutes')}`;
};

const getStatusBadge = (status: CoachingSessionStatus) => {
    let color = 'gray';
    let text: string = status;

    switch (status) {
        case CoachingSessionStatus.UPCOMING:
            color = 'green';
            text = translate('coaching_session.upcoming');
            break;
        case CoachingSessionStatus.COMPLETED:
            color = 'blue';
            text = translate('coaching_session.completed');
            break;
        case CoachingSessionStatus.CANCELED:
            color = 'red';
            text = translate('coaching_session.canceled');
            break;
    }

    return <Badge colorScheme={color}>{text}</Badge>;
};

export const CoachingSessionInformation: React.FC<CoachingSessionInformationProps> = ({ session, cancelSessionMutation, onEditCourt }) => {
    const sportTagBg = useColorModeValue('gray.100', 'gray.700');

    return (
        <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} padding={6}>
                <VStack align="start" spacing={4}>
                    <HStack spacing={3}>
                        <Text fontWeight="bold" minWidth="120px">{translate('coaching_session.coach')}:</Text>
                        <HStack>
                            {session.coach.avatar && (
                                <Avatar size="sm" src={session.coach.avatar} />
                            )}
                            <Text>{`${session.coach.firstName} ${session.coach.lastName}`}</Text>
                        </HStack>
                    </HStack>

                    <HStack justify="space-between" width="100%">
                        <InfoRow
                            label={translate('coaching_session.start_time')}
                            value={formatDate(session.startTime)}
                        />
                        {session.status !== CoachingSessionStatus.CANCELED && Boolean(cancelSessionMutation) && (
                            <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                onClick={() => cancelSessionMutation.mutate(session.id)}
                                isLoading={cancelSessionMutation.isLoading}
                                loadingText={translate('coaching_session.canceling')}
                            >
                                {translate('coaching_session.cancel_session')}
                            </Button>
                        )}
                    </HStack>

                    <InfoRow
                        label={translate('coaching_session.end_time')}
                        value={formatDate(session.endTime)}
                    />

                    <InfoRow
                        label={translate('coaching_session.duration')}
                        value={formatDuration(session.startTime, session.endTime)}
                    />

                    <InfoRow
                        label={translate('coaching_session.participants')}
                        value={session.numberOfPlayers.toString()}
                    />

                    <HStack>
                        <Text fontWeight="bold">{translate('coaching_session.status')}:</Text>
                        {getStatusBadge(session.status)}
                    </HStack>
                </VStack>

                <VStack align="start" spacing={4}>
                    <HStack spacing={3}>
                        <Text fontWeight="bold" minWidth="120px">{translate('coaching_session.student')}:</Text>
                        <HStack>
                            {session.user.avatar && (
                                <Avatar size="sm" src={session.user.avatar} />
                            )}
                            <Text>{`${session.user.firstName} ${session.user.lastName}`}</Text>
                        </HStack>
                    </HStack>

                                        {session.user.email && (
                        <InfoRow
                            label={translate('coaching_session.email')}
                            value={session.user.email}
                        />
                    )}

                    {session.user.level && (
                        <InfoRow
                            label={translate('coaching_session.level')}
                            value={session.user.level.toString()}
                        />
                    )}

                    {session.court && (
                        <HStack justify="space-between" width="100%">
                            <InfoRow
                                label={translate('coaching_session.court')}
                                value={session.court.name}
                            />
                            {onEditCourt && (
                                <Button
                                    size="sm"
                                    colorScheme="brand"
                                    variant="outline"
                                    onClick={onEditCourt}
                                >
                                    {translate('coaching_session.edit_court')}
                                </Button>
                            )}
                        </HStack>
                    )}
                </VStack>
            </SimpleGrid>

            {/* Coach Sports */}
            {session.coach.sports && session.coach.sports.length > 0 && (
                <VStack align="start" spacing={2} paddingX={6}>
                    <Text fontWeight="bold">{translate('coaching_session.coach_sports')}:</Text>
                    <HStack wrap="wrap">
                        {session.coach.sports.map((sport, index) => (
                            <Text key={index} fontSize="sm" bg={sportTagBg} px={2} py={1} borderRadius="md">
                                {sport}
                            </Text>
                        ))}
                    </HStack>
                </VStack>
            )}
        </VStack>
    );
};

interface InfoRowProps {
    label: string;
    value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
    <HStack>
        <Text fontWeight="bold" minWidth="120px">{label}:</Text>
        <Text>{value}</Text>
    </HStack>
);
